import {useCallback, useEffect, useRef, useState} from "react";
import {httpClient} from "../../utils/httpClient";
import {MAX_CASES_PER_REQUEST, runTestsChunk} from "./dlvApi";

const IDLE_STATE = {status: 'idle', cases: [], results: [], chunk: {current: 0, count: 0}, error: null};

function splitInChunks(cases) {
    const chunks = [];
    for (let i = 0; i < cases.length; i += MAX_CASES_PER_REQUEST) {
        chunks.push(cases.slice(i, i + MAX_CASES_PER_REQUEST));
    }
    return chunks;
}

// The backend reports the field relative to the chunk it received (cases[3].expected[1]):
// turn it into something readable that refers to the case number in the whole suite.
function describeField(field, chunkIndex) {
    const match = /^cases\[(\d+)](?:\.(facts|expected)(?:\[(\d+)])?)?/.exec(field ?? '');
    if (!match) {
        return null;
    }
    const caseNumber = chunkIndex * MAX_CASES_PER_REQUEST + Number(match[1]) + 1;
    if (match[2] === 'facts') {
        return `caso ${caseNumber}, fatti`;
    }
    if (match[2] === 'expected') {
        return match[3] === undefined
            ? `caso ${caseNumber}, atomi attesi`
            : `caso ${caseNumber}, atomo atteso ${Number(match[3]) + 1}`;
    }
    return `caso ${caseNumber}`;
}

/**
 * Runs a test suite against the backend in chunks of MAX_CASES_PER_REQUEST cases, one request at a time,
 * collecting the results by position in the whole suite.
 */
export function useDlvTestRun() {
    const [state, setState] = useState(IDLE_STATE);
    const run = useRef({id: 0, cancelSource: null, last: null});

    useEffect(() => {
        const current = run.current;
        return () => {
            current.id++;
            current.cancelSource?.cancel();
        };
    }, []);

    const execute = useCallback(async (cases, programName, fromChunk, previousResults) => {
        const runId = ++run.current.id;
        const chunks = splitInChunks(cases);
        let results = previousResults;
        run.current.last = {cases, programName};
        setState({status: 'running', cases, results, chunk: {current: fromChunk, count: chunks.length}, error: null});

        for (let i = fromChunk; i < chunks.length; i++) {
            setState(s => ({...s, chunk: {current: i, count: chunks.length}}));

            const source = httpClient.cancelToken();
            run.current.cancelSource = source;
            let response;
            try {
                response = await runTestsChunk({programName, cases: chunks[i], cancelToken: source.token});
            } catch (e) {
                if (run.current.id !== runId) {
                    return;
                }
                const where = describeField(e.field, i);
                setState(s => ({
                    ...s,
                    status: 'failed',
                    error: {message: where ? `${e.message} (${where})` : e.message, chunkIndex: i}
                }));
                return;
            }
            if (run.current.id !== runId) {
                return;
            }

            const nextResults = results.slice();
            response.results.forEach(result => {
                const index = i * MAX_CASES_PER_REQUEST + result.index;
                nextResults[index] = {...result, index};
            });
            results = nextResults;
            setState(s => ({...s, results: nextResults}));
        }
        setState(s => ({...s, status: 'finished'}));
    }, []);

    const start = useCallback((cases, programName) => {
        return execute(cases, programName, 0, new Array(cases.length));
    }, [execute]);

    const resume = useCallback(() => {
        const {last} = run.current;
        if (state.status !== 'failed' || !last) {
            return Promise.resolve();
        }
        return execute(last.cases, last.programName, state.error.chunkIndex, state.results);
    }, [execute, state.status, state.error, state.results]);

    const stop = useCallback(() => {
        run.current.id++;
        run.current.cancelSource?.cancel();
        setState(s => s.status === 'running' ? {...s, status: 'stopped'} : s);
    }, []);

    const reset = useCallback(() => {
        run.current.id++;
        run.current.cancelSource?.cancel();
        setState(IDLE_STATE);
    }, []);

    return {state, start, resume, stop, reset};
}

export function countResults(results) {
    let passed = 0;
    let failed = 0;
    results.forEach(result => {
        if (!result) {
            return;
        }
        if (result.passed) {
            passed++;
        } else {
            failed++;
        }
    });
    return {passed, failed};
}
